import { format } from "date-fns";
import { Calendar, Pill, Copy, CreditCard, CheckCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { PrescriptionOrder } from "../../types/prescription";

interface PrescriptionDetailsSectionProps {
  prescription: PrescriptionOrder;
}

export function PrescriptionDetailsSection({
  prescription,
}: PrescriptionDetailsSectionProps) {
  const copyPaymentLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: "Copied",
        description: "Payment link copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy payment link",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">
          Created: {format(new Date(prescription.created_at), "PPp")}
        </span>
      </div>
      {prescription.wati_message_id && (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <span className="text-sm">
            Sent: {format(new Date(prescription.updated_at), "PPp")}
          </span>
        </div>
      )}
      {prescription.total_amount && (
        <div className="flex items-center gap-2">
          <Pill className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            Total: ₹{prescription.total_amount.toFixed(2)}
          </span>
        </div>
      )}
      {prescription.payment_link && (
        <div className="flex items-center gap-2">
          <Copy className="h-4 w-4 text-muted-foreground" />
          <button
            onClick={() => copyPaymentLink(prescription.payment_link!)}
            className="text-sm text-blue-600 hover:underline"
          >
            Copy Payment Link
          </button>
        </div>
      )}
      {prescription.payment_status && (
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">
            Payment: <span className="font-medium">{prescription.payment_status}</span>
          </span>
        </div>
      )}
      {prescription.unmatched_drugs &&
        prescription.unmatched_drugs.length > 0 && (
          <div className="mt-2">
            <span className="text-sm font-medium text-red-600">
              Unmatched Drugs: {prescription.unmatched_drugs.join(", ")}
            </span>
          </div>
        )}
    </div>
  );
}
